import React, { useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';

// Sample values for every LabelVariables marker, mostly taken from a real printed PO label, so
// the preview needs no fetch. A marker missing here shows through as typed, which makes a new
// backend marker without a sample easy to spot.
const SAMPLE_VALUES: Record<string, string> = {
  '{{po_number}}': '382122',
  '{{po_date}}': '09/25/2026',
  '{{vendor_name}}': 'United Refrigeration',
  '{{department}}': 'Service Spot Quotes',
  '{{department_code}}': 'TMSCSQUO',
  '{{entered_by}}': 'FINNEGAN',
  '{{po_creator}}': 'John Finnegan',
  '{{job_id}}': '45120',
  '{{job_desc}}': 'Chiller Replacement',
  '{{order_no}}': '708908',
  '{{order_date}}': '09/25/2026',
  '{{company}}': 'Langeloth Metallurgical Comp',
  '{{address}}': '10 Langeloth Plant Drive',
  '{{city}}': 'Langeloth',
  '{{state}}': 'PA',
  '{{zip}}': '15054',
  '{{warehouse}}': 'Langeloth - Trailer',
  '{{warehouse_shipto}}': 'Langeloth - Trailer',
  '{{warehouse_client_zone}}': 'Pittsburgh Main',
  '{{rack}}': 'R12',
  '{{shelf}}': 'S3',
  '{{bin}}': 'B07',
  '{{received_count}}': '4',
  '{{received_by}}': 'J. Smith',
  '{{received_at}}': '09/29/2026 10:42 AM',
  '{{location}}': 'Pittsburgh',
};

// A 4x6 label printed landscape. Browsers lay out 96 CSS px per inch, so the template's px font
// sizes show at their real size against the label with no scaling.
const LABEL_WIDTH_PX = 6 * 96;
const LABEL_HEIGHT_PX = 4 * 96;

// Only the editor's paragraph spacing and two table rules are added; the rest is browser
// defaults, which is what a plain HTML print gets. Cells never wrap, so every column is as wide
// as its widest cell and the next column starts after it. The print endpoint needs these same
// rules to match.
const LABEL_CSS = `
  html, body { margin: 0; }
  body { box-sizing: border-box; width: ${LABEL_WIDTH_PX}px; height: ${LABEL_HEIGHT_PX}px; padding: 12px; overflow: hidden; }
  p { margin: 0; }
  table { border-collapse: collapse; }
  td { padding: 0 12px 0 0; vertical-align: top; white-space: nowrap; }
`;

const HTML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

// Real ServMan text lands inside the label's HTML, so "AT&T" or a stray "<" must show as typed
// rather than break the layout or inject markup. The samples are known-safe and skip this.
const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

interface LabelPreviewProps {
  body: string;
  // Real values for one PO. Omitted, the preview uses the built-in samples.
  values?: Record<string, string>;
}

const LabelPreview: React.FC<LabelPreviewProps> = ({ body, values }) => {
  const [overflows, setOverflows] = useState(false);

  const srcDoc = useMemo(() => {
    const fill = (marker: string): string => {
      if (!values) return SAMPLE_VALUES[marker] ?? marker;
      return marker in values ? escapeHtml(values[marker]) : marker;
    };
    const filled = body.replace(/\{\{\w+\}\}/g, fill);
    return `<!doctype html><html><head><style>${LABEL_CSS}</style></head><body>${filled}</body></html>`;
  }, [body, values]);

  // Measured after each load: the body is clipped to the label, so anything scrolling past it
  // would be cut off on the printed label too.
  const handleLoad = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const labelBody = event.currentTarget.contentDocument?.body;
    if (!labelBody) return;
    setOverflows(labelBody.scrollHeight > labelBody.clientHeight || labelBody.scrollWidth > labelBody.clientWidth);
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Preview — 4 × 6 in landscape (approximate)
      </Typography>
      {/* The stored body is whatever HTML the API was sent, so it renders in a sandbox that runs
          no scripts. allow-same-origin only lets handleLoad measure it. */}
      <Box
        component="iframe"
        title="Label preview"
        sandbox="allow-same-origin"
        srcDoc={srcDoc}
        onLoad={handleLoad}
        sx={{
          display: 'block',
          width: LABEL_WIDTH_PX,
          height: LABEL_HEIGHT_PX,
          border: '1px solid #bdbdbd',
          borderRadius: 1,
          bgcolor: '#fff',
          boxShadow: 1,
        }}
      />
      {overflows && (
        <Alert severity="warning" sx={{ mt: 1, width: LABEL_WIDTH_PX, boxSizing: 'border-box' }}>
          Content runs past the edge of the label and would be cut off when printed.
        </Alert>
      )}
    </Box>
  );
};

export default LabelPreview;
